import { supabase } from '@/integrations/supabase/client';
import {
  describeTerminalRefusal,
  type TerminalLocationKind,
  type TerminalOutcome,
  type TerminalResult,
} from '@/utils/ticketTerminalLifecycle';

/**
 * 8G.2D.4A — deleting a parent ticket is ONE act.
 *
 * ============================================================================
 * WHAT THIS REPLACES
 * ============================================================================
 * `TicketKebabMenu.handleDelete` used to be the transaction coordinator:
 *
 *     const results = await Promise.allSettled(
 *       descendants.map(d => cancelTicketChain(d.id, 'parent-deleted')));
 *     if (results.some(rejected)) { toast('Deletion aborted.'); return; }
 *     await deleteTicketIfQuiescent(ticketId, reason);
 *
 * Every one of those calls was its own committed transaction, which produced
 * two distinct partial commits — both measured before the repair:
 *
 *   ONE BLOCKED DESCENDANT. `allSettled` does not short-circuit, so the other
 *   descendants were cancelled anyway and the parent was never deleted:
 *       source=Active D1=Cancelled D2=Active(PAUSED_WORK) D3=Cancelled
 *   "Deletion aborted" was announced AFTER two children had already died.
 *
 *   A BLOCKED PARENT. Every descendant cancelled, and only then did the parent
 *   answer PAUSED_WORK:
 *       source=Active D1=Cancelled D2=Cancelled
 *   The children were terminalized to make room for a deletion that never
 *   happened.
 *
 * The server now owns the whole act. The browser sends one ticket and one
 * reason; membership, the actor, the shop and every terminal timestamp are
 * derived under the source's transition lock.
 *
 * ============================================================================
 * THE DESCENDANTS ARE NOT THE CALLER'S TO NAME
 * ============================================================================
 * There is deliberately no `descendantIds` parameter. The client's list is a
 * projection read at some earlier moment; the authority derives the family
 * itself, under the lock, from the same relation the product has always used
 * (`rollover_of_ticket_id` / `parent_ticket_id`, live statuses only). It is
 * NOT recursive — see the migration for why a transitive closure would be a
 * guess rather than a rule.
 */

type Rpc = (fn: string, args: Record<string, unknown>) => Promise<{
  data: unknown;
  error: { message: string } | null;
}>;

// RECEIVER-PRESERVING. `SupabaseClient.rpc` is a prototype method whose body is
// `return this.rest.rpc(...)`, so detaching it —
//
//     const rpc = supabase.rpc as unknown as Rpc;   // <- what this used to be
//
// — drops the receiver and throws `TypeError: Cannot read properties of
// undefined (reading 'rest')` before the request is ever sent. That is not
// theoretical: it is what production returned for the timer state read and the
// ticket delete, captured with http_status = null because nothing was sent.
//
// The wrapper below calls the method ON the client, so the receiver survives.
// Every call site is unchanged.
const rpc: Rpc = (fn, args) => (supabase.rpc as unknown as Rpc)(fn, args);

export type DeleteFamilyOutcome =
  /** The source is Deleted and every live direct descendant is Cancelled. */
  | 'FAMILY_DELETED'
  /** A repeat after a lost response. Nothing was written a second time. */
  | 'ALREADY_APPLIED'
  /** The linked tickets changed while the delete was starting. */
  | 'FAMILY_CHANGED_RETRY'
  /** Continuation lineage points outside this shop. A person must resolve it. */
  | 'FAMILY_LINEAGE_INVALID'
  /** Every refusal the single-ticket terminal authority can give, unchanged. */
  | Exclude<TerminalOutcome, 'TERMINALIZED' | 'ALREADY_TERMINAL'>;

export interface DeleteFamilyResult {
  outcome: DeleteFamilyOutcome;
  ticketId: string;
  /** Ids cancelled by THIS call. Empty on a retry — the act already happened. */
  cancelled: string[];
  /** Whether the SOURCE or one of its descendants held the act up. */
  blockedMemberRole: 'SOURCE' | 'DESCENDANT' | null;
  /** Which ticket refused. Always inside the caller's own shop. */
  blockedTicketId: string | null;
  activeSessions: number;
  unresolvedPauses: number;
  awaitingReview: number;
  note: string | null;
}

const KNOWN: DeleteFamilyOutcome[] = [
  'FAMILY_DELETED', 'ALREADY_APPLIED', 'FAMILY_CHANGED_RETRY',
  'FAMILY_LINEAGE_INVALID', 'ACTIVE_WORK', 'PAUSED_WORK', 'REVIEW_REQUIRED',
  'TIMER_INTEGRITY_BLOCK', 'LOCATION_REQUIRED', 'LOCATION_CONFLICT',
  'TICKET_NOT_TERMINALIZABLE', 'TICKET_NOT_AVAILABLE',
  'TERMINAL_EVIDENCE_CONFLICT', 'ROLLOVER_CHAIN_MEMBER', 'SUBMIT_FAILED',
];

/**
 * Delete a parent ticket and cancel its live direct continuations, atomically.
 *
 * `SUBMIT_FAILED` means the call produced no server answer. It does NOT mean
 * nothing committed — the act may have succeeded and the response been lost.
 * Retry with the same ticket id: the authority answers ALREADY_APPLIED, which
 * is a success.
 */
export async function deleteTicketFamily(
  ticketId: string,
  reason?: string | null,
  locationKind: TerminalLocationKind | null = null,
  parkingSpotId: string | null = null,
): Promise<DeleteFamilyResult> {
  const { data, error } = await rpc('delete_ticket_family_if_quiescent', {
    p_ticket_id: ticketId,
    p_reason: reason ?? null,
    p_location_kind: locationKind,
    p_parking_spot_id: locationKind === 'PARKING_SPOT' ? (parkingSpotId ?? null) : null,
  });

  if (error) {
    console.warn('[deleteTicketFamily] no server answer', error);
    return {
      outcome: 'SUBMIT_FAILED', ticketId, cancelled: [],
      blockedMemberRole: null, blockedTicketId: null,
      activeSessions: 0, unresolvedPauses: 0, awaitingReview: 0,
      note: error.message,
    };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  // Unknown outcomes FAIL CLOSED. The vocabulary belongs to the server, and a
  // client that guesses at an answer it does not recognise is how a refusal
  // gets reported as a success.
  const outcome = KNOWN.includes(r.outcome as DeleteFamilyOutcome)
    ? (r.outcome as DeleteFamilyOutcome)
    : 'SUBMIT_FAILED';
  const role = r.blocked_member_role;

  return {
    outcome,
    ticketId: (r.ticket_id as string) ?? ticketId,
    cancelled: Array.isArray(r.cancelled) ? (r.cancelled as string[]) : [],
    blockedMemberRole: role === 'SOURCE' || role === 'DESCENDANT' ? role : null,
    blockedTicketId: (r.blocked_ticket_id as string) ?? null,
    activeSessions: Number(r.active_sessions ?? 0),
    unresolvedPauses: Number(r.unresolved_pauses ?? 0),
    awaitingReview: Number(r.awaiting_review ?? 0),
    note: (r.note as string) ?? null,
  };
}

/** Whether the family is settled, whoever settled it. */
export function isFamilyDeleteSettled(o: DeleteFamilyOutcome): boolean {
  return o === 'FAMILY_DELETED' || o === 'ALREADY_APPLIED';
}

/**
 * Did THIS call perform the act, or find it already done?
 *
 * Both are successes for STATE, and the difference matters for everything that
 * is not state. A retry after a lost response must not emit the audit trail of
 * a deletion that already happened once.
 */
export function didDeleteFamily(o: DeleteFamilyOutcome): boolean {
  return o === 'FAMILY_DELETED';
}

/**
 * Why the delete was refused, in the operator's language.
 *
 * The single-ticket refusals reuse `describeTerminalRefusal` verbatim rather
 * than restating it — one refusal vocabulary, not two that drift. What is
 * added here is WHOSE problem it is: the same PAUSED_WORK means something
 * different to the operator depending on whether it is the ticket they clicked
 * or one linked to it.
 */
export function describeFamilyDeleteRefusal(r: DeleteFamilyResult): string {
  if (r.outcome === 'FAMILY_CHANGED_RETRY') {
    return 'The linked tickets changed while this was starting. Try again.';
  }
  if (r.outcome === 'FAMILY_LINEAGE_INVALID') {
    return r.note ?? 'This ticket is linked to another shop. Someone needs to sort that out first.';
  }
  if (r.outcome === 'SUBMIT_FAILED') {
    return 'The result could not be confirmed. Try again.';
  }

  const base = describeTerminalRefusal({
    outcome: r.outcome as TerminalOutcome,
    ticketId: r.blockedTicketId ?? r.ticketId,
    activeSessions: r.activeSessions,
    unresolvedPauses: r.unresolvedPauses,
    awaitingReview: r.awaitingReview,
    note: r.note,
  } as TerminalResult);

  if (r.blockedMemberRole === 'DESCENDANT') {
    // Naming the linked ticket is the point: without it the operator is told
    // a technician is paused on a ticket that visibly has nobody on it.
    return `A linked ticket${r.blockedTicketId ? ` (${r.blockedTicketId})` : ''} is not ready. ${base}`;
  }
  return base;
}
